import React, { useEffect, useState } from 'react';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { stripVariantSuffix } from '@/components/customercenter-ui/utils/formatUtils';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { createRecommendClient } from '@/helpers/algolia/algoliaRecommendV5';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import AlgoliaRecommendProductsList from './algoliaRecommendProductsList';
import { AlsoBoughtContentProps } from '../../../../../../types/algolia/AlsoBoughtContentProps';

const AlsoBoughtContent: React.FC<AlsoBoughtContentProps> = ({
  siteId,
  maxAlsoBoughtContentPerPage,
  data,
  setProductsStatus,
}) => {
  const [alsoBoughtItems, setAlsoBoughtItems] = useState<any>(undefined);
  const objectID = data?.data?.data?.dataSource?.product?.productId ?? '';
  const projectSettings: ProjectSettings = useProjSettings();
  const algoliaAppId = projectSettings?.algoliaAppId;
  const algoliaApiKey = projectSettings?.algoliaApiKey;

  useEffect(() => {
    if (!siteId || !algoliaAppId || !algoliaApiKey || !objectID) {
      setProductsStatus(false);
      return;
    }

    const virtualAlgoliaConfig = projectSettings?.isVirtualAlgoliaEnabled
      ? JSON.parse(projectSettings.isVirtualAlgoliaEnabled)
      : {};
    const isVirtualAlgoliaEnabledSite = virtualAlgoliaConfig?.[`${getSelectedDivision()}Site`];
    const dynamicIndexName =
      isVirtualAlgoliaEnabledSite === true || isVirtualAlgoliaEnabledSite === 'true'
        ? `products_${siteId}_CSA`
        : `products_${siteId}`;
    const recommendClient = createRecommendClient(algoliaAppId, algoliaApiKey);

    if (!recommendClient) {
      return;
    }

    let isCancelled = false;
    const recommendationQueries = [
      {
        indexName: dynamicIndexName,
        objectID: `${stripVariantSuffix(objectID)}`,
        maxRecommendations: maxAlsoBoughtContentPerPage ?? 15,
        threshold: 0,
        queryParameters: {
          filters: 'isInStock:true',
          clickAnalytics: true,
        },
      },
    ];

    recommendClient
      .getRecommendations({
        requests: recommendationQueries.map((query) => ({
          ...query,
          model: 'bought-together' as const,
          queryParameters: {
            ...query.queryParameters,
            clickAnalytics: true,
          },
        })),
      })
      .then((recommendations: any) => {
        if (isCancelled) {
          return;
        }

        if (recommendations?.results) {
          const alsoBoughtItems: any[] = [];
          recommendations?.results
            ?.filter((item: any, index: any) => {
              return recommendations?.results?.indexOf(item) === index;
            })
            ?.forEach((item: any) => {
              if (item?.hits && item?.hits?.length > 0) {
                alsoBoughtItems.push(
                  ...item.hits.map((hit: any, index: number) => {
                    return {
                      ...hit,
                      ['__queryID']: item?.queryID,
                      ['__position']: index + 1,
                    };
                  }),
                );
              }
            });
          setAlsoBoughtItems(alsoBoughtItems);
          setProductsStatus(alsoBoughtItems?.length > 0);
        }
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return;
        }
        console.error('Error while fetching data:', error);
        setProductsStatus(false);
        setAlsoBoughtItems(undefined);
      });

    return () => {
      isCancelled = true;
    };
  }, [objectID, siteId, algoliaAppId, algoliaApiKey, maxAlsoBoughtContentPerPage]);

  return (
    <section id="alsoBoughtItems">{alsoBoughtItems && <AlgoliaRecommendProductsList item={alsoBoughtItems} />}</section>
  );
};

export default AlsoBoughtContent;
